import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import LucideIcon from "../../components/LucideIcon";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError } from "../../utils";
import ScheduleTerminationDialog from "./dialogs/ScheduleTerminationDialog";
import CancelTerminationDialog from "./dialogs/CancelTerminationDialog";

interface CompanyTerminationsProps {
  getToken: () => Promise<string>;
}

interface ScheduledCompany {
  id: string;
  name: string | null;
  terminationScheduledAt: string;
  terminationScheduledBy: string | null;
  terminationReason: string | null;
  alreadySoftDeleted: boolean;
}

interface ListScheduledResponse {
  gracePeriodDays: number;
  companies: ScheduledCompany[];
}

interface CompanyLookup {
  id: string;
  name: string | null;
}

// Daysuntil cutoff = scheduled date + grace window − now. Negative = past cutoff,
// soft-delete pending the next prune-job run (~03:45 UTC daily).
const computeDaysUntilCutoff = (scheduledAt: string, gracePeriodDays: number) => {
  const cutoff = moment(scheduledAt).add(gracePeriodDays, "days");
  return cutoff.diff(moment(), "days");
};

const formatScheduledAt = (iso: string) => moment(iso).format("MMM D, YYYY h:mm A");

const CompanyTerminations: React.FC<CompanyTerminationsProps> = ({ getToken }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [gracePeriodDays, setGracePeriodDays] = useState(30);
  const [scheduled, setScheduled] = useState<ScheduledCompany[]>([]);

  // Schedule-by-id form: admin pastes a company ID + reason and clicks Schedule.
  // (We don't render a full company picker here because the existing Companies
  // admin screen is where admins discover IDs.)
  const [scheduleCompanyId, setScheduleCompanyId] = useState("");
  const [scheduleCompanyLookup, setScheduleCompanyLookup] = useState<CompanyLookup | null>(null);
  const [lookingUpCompany, setLookingUpCompany] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  const [cancelTarget, setCancelTarget] = useState<ScheduledCompany | null>(null);

  const loadScheduled = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setRefreshing(true);
      setError(null);
      try {
        const token = await getToken();
        const response = await axios.get<ListScheduledResponse>(
          `${hostname}/companies/admin/terminations`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setScheduled(response.data.companies || []);
        setGracePeriodDays(response.data.gracePeriodDays || 30);
      } catch (err) {
        setError(parseError(err));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [getToken]
  );

  useEffect(() => {
    loadScheduled(false);
  }, [loadScheduled]);

  // Resolve a typed company ID against the admin company list so the schedule
  // dialog can show the company name instead of a bare UUID.
  const lookupCompany = useCallback(
    async (companyId: string) => {
      if (!companyId) {
        setScheduleCompanyLookup(null);
        return;
      }
      setLookingUpCompany(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/companies/admin/${encodeURIComponent(companyId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const company = response.data?.company || response.data;
        setScheduleCompanyLookup({ id: company.id, name: company.name ?? null });
      } catch {
        setScheduleCompanyLookup(null);
      } finally {
        setLookingUpCompany(false);
      }
    },
    [getToken]
  );

  const handleOpenScheduleDialog = async () => {
    const trimmed = scheduleCompanyId.trim();
    if (!trimmed) {
      setError("Enter a company ID first.");
      return;
    }
    setError(null);
    await lookupCompany(trimmed);
    setScheduleDialogOpen(true);
  };

  const handleScheduleConfirm = async (reason: string) => {
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/companies/admin/${encodeURIComponent(scheduleCompanyId.trim())}/termination`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(
        `Scheduled termination for ${scheduleCompanyLookup?.name || scheduleCompanyId}.`
      );
      setScheduleCompanyId("");
      setScheduleCompanyLookup(null);
      await loadScheduled(false);
    } catch (err) {
      setError(parseError(err));
      throw err; // re-throw so the dialog stays open on failure
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/companies/admin/${encodeURIComponent(cancelTarget.id)}/termination`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(`Cancelled termination for ${cancelTarget.name || cancelTarget.id}.`);
      await loadScheduled(false);
    } catch (err) {
      setError(parseError(err));
      throw err;
    }
  };

  const sortedScheduled = useMemo(
    () =>
      [...scheduled].sort((a, b) =>
        moment(a.terminationScheduledAt).diff(moment(b.terminationScheduledAt))
      ),
    [scheduled]
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Company terminations
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Schedule a company for deletion {gracePeriodDays} days from now (Data
            Management Policy Appendix A.1). Reversible during the grace window. The
            daily prune job runs at 03:45 UTC.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => loadScheduled(true)} disabled={refreshing}>
              {refreshing ? <CircularProgress size={20} /> : <LucideIcon name="RefreshCw" size={20} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Schedule a termination
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
          <TextField
            size="small"
            label="Company ID"
            value={scheduleCompanyId}
            onChange={(e) => setScheduleCompanyId(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            placeholder="vlgjdhmx9n"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LucideIcon name="Building2" size={16} />
                </InputAdornment>
              ),
            }}
          />
          <LoadingButton
            variant="contained"
            color="error"
            loading={lookingUpCompany}
            onClick={handleOpenScheduleDialog}
            startIcon={<LucideIcon name="CalendarClock" size={18} />}
          >
            Schedule
          </LoadingButton>
        </Stack>
      </Paper>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Currently scheduled ({sortedScheduled.length})
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : sortedScheduled.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No companies are currently scheduled for termination.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Scheduled at</TableCell>
                <TableCell>Days until cutoff</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedScheduled.map((row) => {
                const daysUntil = computeDaysUntilCutoff(
                  row.terminationScheduledAt,
                  gracePeriodDays
                );
                const past = daysUntil < 0;
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.name || "(unnamed)"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {row.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatScheduledAt(row.terminationScheduledAt)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={past ? "error" : daysUntil <= 7 ? "warning" : "default"}
                        label={past ? `${Math.abs(daysUntil)}d overdue` : `${daysUntil}d`}
                      />
                    </TableCell>
                    <TableCell>
                      {row.alreadySoftDeleted ? (
                        <Chip size="small" color="error" label="Soft-deleted" />
                      ) : (
                        <Chip size="small" color="primary" label="Pending" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.terminationReason || ""}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 240,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: row.terminationReason ? "text.primary" : "text.secondary",
                          }}
                        >
                          {row.terminationReason || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {!row.alreadySoftDeleted && (
                        <LoadingButton
                          size="small"
                          startIcon={<LucideIcon name="X" size={16} />}
                          onClick={() => setCancelTarget(row)}
                        >
                          Cancel
                        </LoadingButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ScheduleTerminationDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        onConfirm={handleScheduleConfirm}
        companyName={scheduleCompanyLookup?.name || ""}
        companyId={scheduleCompanyId}
        gracePeriodDays={gracePeriodDays}
      />
      <CancelTerminationDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
        companyName={cancelTarget?.name || ""}
        companyId={cancelTarget?.id || ""}
      />
    </Box>
  );
};

export default CompanyTerminations;
